import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { REQUEST_TYPES, COMMITTEES, DISTRIBUTION_CHANNELS, PRIORITIES } from "@/lib/constants";
import { currentSkin } from "@/lib/skin";
import { FileUpload } from "@/components/FileUpload";
import { AIAssistButton } from "@/components/AIAssistButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, Loader2, Save, Send, AlertTriangle, CheckCircle2, ShieldCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { differenceInCalendarDays, addBusinessDays, format } from "date-fns";

const empty = {
  title: "", request_type: "Social Media Graphic/Flyer", request_type_other: "", committee: "", project_purpose: "", target_audience: "",
  deliverables: "", final_wording: "", event_info: "", event_date: "", location: "", registration_link: "",
  distribution_channels: [], desired_release_date: "", final_deadline: "", priority: "normal",
  leadership_approval_needed: false, printing_needed: false, printing_specs: "", budget: "",
  include_in_sunday_news: false, call_to_action: "", category: "Announcement",
};

const Field = ({ label, children, required, hint }) => (
  <div>
    <Label className="flex items-center gap-1">{label}{required && <span className="text-accent">*</span>}</Label>
    {hint && <p className="mb-1.5 text-xs text-muted-foreground">{hint}</p>}
    <div className="mt-1.5">{children}</div>
  </div>
);

// production timelines in days per request type
const TIMELINES = {
  "Social Media Graphic/Flyer": 5, "Brochure": 7, "Toolkit": 10, "Quarterly Newsletter": 14,
  "Presentation": 7, "Press Release/Media Advisory": 5, "Multi Channel Campaign": 14, "Other (explain)": 7,
};

export default function CommsRequestForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, features } = useAuth();
  const [form, setForm] = useState({ ...empty, committee: user?.committee || "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(!id);

  useEffect(() => {
    if (id) {
      api.get(`/submissions/${id}`).then((r) => {
        setForm({ ...empty, ...r.data.data });
        setFiles(r.data.data.attachments || []);
        setLoaded(true);
      }).catch(() => { toast.error("Could not load submission"); navigate("/submissions"); });
    }
  }, [id, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (ch) => setForm((f) => ({
    ...f, distribution_channels: f.distribution_channels.includes(ch)
      ? f.distribution_channels.filter((c) => c !== ch) : [...f.distribution_channels, ch],
  }));

  const requiredDays = TIMELINES[form.request_type] || 5;
  const rush = form.priority === "rush";
  const leadDays = rush ? Math.max(2, Math.ceil(requiredDays / 2)) : requiredDays;
  const estimatedCompletion = addBusinessDays(new Date(), leadDays);
  let deadlineWarning = null;
  if (form.desired_release_date) {
    const days = differenceInCalendarDays(new Date(form.desired_release_date), new Date());
    if (days < requiredDays) {
      deadlineWarning = `This ${form.request_type} typically needs ${requiredDays} business days of production time, but your requested date is only ${days} day(s) away. This may require a rush request.`;
    }
  }

  const save = async (status) => {
    if (status === "submitted" && (!form.project_purpose || !form.deliverables || !form.committee)) {
      toast.error("Committee/office, project purpose, and deliverables are required to submit");
      return;
    }
    setSaving(true);
    const data = { ...form, attachments: files, title: form.title || form.project_purpose?.slice(0, 60),
      estimated_completion: format(estimatedCompletion, "yyyy-MM-dd"), production_lead_days: leadDays };
    try {
      if (id) await api.put(`/submissions/${id}`, { submission_type: "comms_request", data, status });
      else await api.post("/submissions", { submission_type: "comms_request", data, status });
      toast.success(status === "draft" ? "Saved as draft" : "Communications request submitted!");
      navigate("/submissions");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="comms-request-form">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ backgroundColor: "hsl(345 48% 70%)" }}><Palette className="h-5 w-5" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Communications Request</h1>
          <p className="text-sm text-muted-foreground">For creative, editorial, and promotional support.</p>
        </div>
      </div>

      <Card className="space-y-5 p-6">
        <h2 className="font-heading text-lg font-semibold">Request details</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Request type" required>
            <Select value={form.request_type} onValueChange={(v) => set("request_type", v)}>
              <SelectTrigger data-testid="cr-type"><SelectValue /></SelectTrigger>
              <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Committee or Office Submitting Request" required>
            <Input data-testid="cr-committee" value={form.committee} onChange={(e) => set("committee", e.target.value)} placeholder="Enter committee or office" />
          </Field>
        </div>
        {form.request_type === "Other (explain)" && (
          <Field label="Please explain your request type" required>
            <Input data-testid="cr-type-other" value={form.request_type_other} onChange={(e) => set("request_type_other", e.target.value)} placeholder="Describe the type of support you need" />
          </Field>
        )}
        <Field label="Project title / purpose" required>
          <Input data-testid="cr-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Short project name" />
        </Field>
        <Field label="Project purpose & goals" required hint="What are you trying to accomplish?">
          <Textarea data-testid="cr-purpose" rows={3} value={form.project_purpose} onChange={(e) => set("project_purpose", e.target.value)} />
        </Field>
        <Field label="Target audience"><Input data-testid="cr-audience" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} placeholder="e.g. All members, community, youth" /></Field>
        <Field label="Requested deliverables" required hint="What specifically do you need created?">
          <Textarea data-testid="cr-deliverables" rows={2} value={form.deliverables} onChange={(e) => set("deliverables", e.target.value)} placeholder="e.g. One 8.5x11 flyer + Instagram graphic" />
        </Field>
      </Card>

      <Card className="space-y-5 p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Content & wording</h2>
          <AIAssistButton testId="cr-ai" getText={() => form.final_wording} onApply={(v) => set("final_wording", v)} />
        </div>
        <Field label="Final approved wording" hint="Copy that must appear exactly (if any)">
          <Textarea data-testid="cr-wording" rows={4} value={form.final_wording} onChange={(e) => set("final_wording", e.target.value)} />
        </Field>
        <Field label="Call to action"><Input data-testid="cr-cta" value={form.call_to_action} onChange={(e) => set("call_to_action", e.target.value)} /></Field>
        <Field label="Event information (if applicable)"><Textarea data-testid="cr-event-info" rows={2} value={form.event_info} onChange={(e) => set("event_info", e.target.value)} placeholder="Date, time, location…" /></Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Event date"><Input type="date" data-testid="cr-event-date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} /></Field>
          <Field label="Registration link"><Input data-testid="cr-reg-link" value={form.registration_link} onChange={(e) => set("registration_link", e.target.value)} placeholder="https://" /></Field>
        </div>
        <Field label="Distribution channels">
          <div className="flex flex-wrap gap-2" data-testid="cr-channels">
            {DISTRIBUTION_CHANNELS.filter((ch) => (features.publication_builder || ch !== "Sunday News") && (currentSkin() === "aka" || !["Internal Communications", "Media/Press"].includes(ch))).map((ch) => (
              <button type="button" key={ch} onClick={() => toggleChannel(ch)}
                data-testid={`cr-channel-${ch.replace(/\s+/g, "-")}`}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${form.distribution_channels.includes(ch) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}>
                {ch}
              </button>
            ))}
          </div>
        </Field>
      </Card>

      <Card className="space-y-5 p-6">
        <h2 className="font-heading text-lg font-semibold">Timeline & specs</h2>
        <div className="grid gap-5 sm:grid-cols-3">
          <Field label="Desired release date"><Input type="date" data-testid="cr-release-date" value={form.desired_release_date} onChange={(e) => set("desired_release_date", e.target.value)} /></Field>
          <Field label="Final deadline"><Input type="date" data-testid="cr-deadline" value={form.final_deadline} onChange={(e) => set("final_deadline", e.target.value)} /></Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="cr-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        {deadlineWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="cr-deadline-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><p>{deadlineWarning}</p>
          </div>
        )}
        {form.desired_release_date && !deadlineWarning && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary" data-testid="cr-deadline-ok">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /><p>Your requested date meets the {requiredDays}-business-day production timeline for a {form.request_type}.</p>
          </div>
        )}
        <div className="flex items-start gap-3 rounded-lg border p-3 text-sm" data-testid="cr-estimated-completion"
          style={{ backgroundColor: "hsl(345 48% 97%)", borderColor: "hsl(345 48% 82%)" }}>
          <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "hsl(345 48% 55%)" }} />
          <div>
            <p className="font-semibold text-foreground">Estimated completion: {format(estimatedCompletion, "EEEE, MMMM d, yyyy")}</p>
            <p className="mt-0.5 text-muted-foreground">
              Based on the standard {leadDays}-business-day production timeline for a {form.request_type}{rush ? " (expedited for rush priority)" : ""}. Final date is confirmed by Communications after leadership approval.
            </p>
          </div>
        </div>
        <Field label="Attach photos, logos & documents"><FileUpload testId="cr-files" files={files} onChange={setFiles} /></Field>
      </Card>

      <Card className="space-y-4 p-6">
        {form.committee && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-primary" data-testid="cr-routing-note">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
            <p>This request will be reviewed by chapter leadership (Chapter President or Vice President) before it is released to Communications for production.</p>
          </div>
        )}
        {features.publication_builder && (
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-accent/40 bg-accent/5 p-4" data-testid="cr-include-news-label">
            <Checkbox checked={form.include_in_sunday_news} onCheckedChange={(v) => set("include_in_sunday_news", !!v)} data-testid="cr-include-news" className="mt-0.5" />
            <div>
              <p className="font-medium">Include this project in Sunday News</p>
              <p className="text-xs text-muted-foreground">Once approved, we'll create a connected Sunday News item automatically — no need to enter the information twice.</p>
            </div>
          </label>
        )}
      </Card>

      <div className="sticky bottom-4 flex gap-3 rounded-xl border border-border bg-card/95 p-3 backdrop-blur">
        <Button variant={currentSkin() === "aka" ? "default" : "outline"} onClick={() => save("draft")} disabled={saving} data-testid="cr-save-draft" className={`flex-1 ${currentSkin() === "aka" ? "bg-green-700 text-white hover:bg-green-800" : ""}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save draft</>}
        </Button>
        <Button onClick={() => save("submitted")} disabled={saving} data-testid="cr-submit" className="flex-1 border-0 text-white hover:opacity-90" style={{ backgroundColor: "hsl(345 48% 70%)" }}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Submit request</>}
        </Button>
      </div>
    </div>
  );
}
